import { useEffect, useRef } from 'react'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentGameId, useGetCurrentGame, useGetCurrentLanguage } from 'src/store/game'
import { isMarbleGame } from 'src/types/domain/Game'
import { useNotificationActions } from 'src/features/notification/store'
import { freeBetGroupToNotification } from 'src/features/notification/factories/notifications.factories'
import { useInventoryStore } from '../store'
import { planFreeBetAutoClaim } from '../utils/planFreeBetAutoClaim'
import { isDebugItem } from '../utils/isDebugItem'
import { useClaimInventoryItem } from './useClaimInventoryItem'

// A real grant of N free bets arrives as N separate `inventory-item:added`
// events in a burst. We wait for that burst to settle, then claim them all and
// show a single aggregated banner instead of one banner per bet.
const AUTO_CLAIM_DEBOUNCE_MS = 500

/**
 * Auto-claims claimable free bets applicable to the current game and announces
 * each grant with one aggregated, informational banner (no Claim button).
 * De-dupes per id so a re-render / reconnect / re-broadcast can never claim the
 * same bet twice. Mount once, alongside the notification stack.
 */
export const useAutoClaimFreeBets = () => {
  const items = useInventoryStore((state) => state.items)
  const currentGameId = useGetCurrentGameId()
  const currentGame = useGetCurrentGame()
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const { addNotification } = useNotificationActions()
  const { claimInventoryItem } = useClaimInventoryItem()
  const storeClaimItem = useInventoryStore((state) => state.actions.claimItem)

  // Ids we've already kicked off a claim for — the double-claim guard.
  const claimAttemptedRef = useRef<Set<string>>(new Set())
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Latest non-item deps, read at fire time without widening the effect deps
  // (which would reset the debounce on every language/game change).
  const depsRef = useRef({
    currentGameId,
    currentGame,
    t,
    addNotification,
    claimInventoryItem,
    storeClaimItem
  })
  depsRef.current = {
    currentGameId,
    currentGame,
    t,
    addNotification,
    claimInventoryItem,
    storeClaimItem
  }

  // v12.4 ask 6: the game TYPE gates the whole plan — crash games cannot
  // wager free bets (the backend hard-rejects them), so nothing is claimed
  // or bannered while the player is on one (see planFreeBetAutoClaim).
  const currentGameType = isMarbleGame(currentGame) ? currentGame.track.gameType : undefined

  useEffect(() => {
    // Anything new to claim? If not, don't (re)arm the debounce.
    const pending = planFreeBetAutoClaim(
      items,
      currentGameId,
      claimAttemptedRef.current,
      currentGameType
    ).toClaim
    if (pending.length === 0) return undefined

    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      const deps = depsRef.current
      // Read the freshest snapshot — the burst may still have been arriving.
      const { toClaim, groups } = planFreeBetAutoClaim(
        useInventoryStore.getState().items,
        deps.currentGameId,
        claimAttemptedRef.current,
        isMarbleGame(deps.currentGame) ? deps.currentGame.track.gameType : undefined
      )
      if (toClaim.length === 0) return

      // Mark attempted up-front so nothing claims these ids again.
      toClaim.forEach((item) => claimAttemptedRef.current.add(item.id))

      // `track.name` is the display name ("Marble Plinko"); `game.name` is the configuration ("Classic").
      const gameName = isMarbleGame(deps.currentGame) ? deps.currentGame.track.name : undefined
      groups.forEach((group) => {
        deps.addNotification(freeBetGroupToNotification(group, deps.t, gameName), false)
      })

      toClaim.forEach((item) => {
        if (isDebugItem(item)) {
          deps.storeClaimItem(item.id)
        } else {
          deps.claimInventoryItem(item.id).catch((error) => {
            console.error('Auto-claim failed:', error)
          })
        }
      })
    }, AUTO_CLAIM_DEBOUNCE_MS)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [items, currentGameId, currentGameType])

  // Reset the guard on unmount so a fresh session re-evaluates from scratch.
  useEffect(
    () => () => {
      claimAttemptedRef.current.clear()
    },
    []
  )
}
